<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicInput from '~/components/form-elements/BasicInput.vue'
import BasicInputDecorator from '~/components/form-elements/BasicInputDecorator.vue'
import ConfirmDiscountSelection from '~/components/modal-windows/ConfirmDiscountSelection.vue'
import constants from '~/constants'
import { DiscountType, DiscountTypeUtils } from '~/enums/discount-type'
import { Mask, MaskUtils } from '~/enums/mask'
import helpers from '~/helpers'
import formatter from '~/helpers/formatter'
import { useCartStore } from '~/store/cart'

const maskaOptions = { number: MaskUtils.getMaskaOptions(Mask.Number) }
const messages = {
  // Promo code.
  promoCodeFieldCannotBeEmpty: 'Поле «Промокод» не может быть пустым.',

  // Points.
  failedToApplyPoints: 'Не удалось применить баллы iTAB Плюс.',
  maximumNumberOfPointsForOrderHasBeenExceeded: 'Превышено максимальное количество баллов iTAB Плюс на заказ.',
  pointsFieldCannotBeEmpty: 'Поле «Баллы iTAB Плюс для списания» не может быть пустым.',
  pointsToSpendMustBePositiveIntegers: 'Баллы iTAB Плюс для списания должны быть положительными целыми числами.',
  pointsWillBeRedeemed: 'Баллы iTAB Плюс будут списаны.',

  // Supermins points.
  failedToApplySuperminsPoints: 'Не удалось применить баллы Supermins.',
  maximumNumberOfSuperminsPointsForOrderHasBeenExceeded: 'Превышено максимальное количество баллов Supermins на заказ.',
  superminsPointsFieldCannotBeEmpty: 'Поле «Баллы Supermins для списания» не может быть пустым.',
  superminsPointsToSpendMustBePositiveIntegers: 'Баллы Supermins для списания должны быть положительными целыми числами.',
  superminsPointsWillBeRedeemed: 'Баллы Supermins будут списаны.',

  // Certificate.
  failedToApplyCertificate: 'Не удалось применить сертификат.',
  certificateFieldCannotBeEmpty: 'Поле «Код активации сертификата» не может быть пустым.',
  certificateIsApplied: 'Сертификат применён.'
}

const { $toast } = useNuxtApp()
const cartStore = useCartStore()

const discountType = ref(DiscountType.PromoCode)
const hoveredCertificate = ref(false)
const hoveredPoints = ref(false)
const hoveredPoints2 = ref(false)
const hoveredPromoCode = ref(false)
const showCertificateBlock = ref(false)

const discountTypes = computed(
  () => DiscountTypeUtils.sortedMembers.filter(
    (x: DiscountType): boolean => {
      switch (x) {
        case discountType.value:
          return false
        case DiscountType.Points:
          return cartStore.maxPointsForOrder !== null && cartStore.maxPointsForOrder > 0
        case DiscountType.SuperminsPoints:
          return cartStore.maxSuperminsPointsForOrder !== null && cartStore.maxSuperminsPointsForOrder > 0
        default:
          return true
      }
    }
  )
)

async function applyCertificate (): Promise<void> {
  const { failedToApplyCertificate, certificateFieldCannotBeEmpty, certificateIsApplied } = messages

  if (!cartStore.form.certificateName) {
    $toast.error(certificateFieldCannotBeEmpty)

    return
  }

  cartStore.isCertificateApplying = true
  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  if (cartStore.isDataLoadingError) {
    $toast.error(failedToApplyCertificate)
  } else if (cartStore.errorDetail) {
    $toast.error(cartStore.errorDetail)
  } else {
    cartStore.isCertificateApplied = true

    $toast.success(certificateIsApplied)
  }

  cartStore.isDataLoading2 = false
  cartStore.isCertificateApplying = false
}

async function applyPoints (isSupermins = false): Promise<void> {
  const { pointsToSpend, superminsPointsToSpend } = cartStore.form
  const {
    // Points.
    failedToApplyPoints,
    maximumNumberOfPointsForOrderHasBeenExceeded,
    pointsFieldCannotBeEmpty,
    pointsToSpendMustBePositiveIntegers,
    pointsWillBeRedeemed,

    // Supermins points.
    failedToApplySuperminsPoints,
    maximumNumberOfSuperminsPointsForOrderHasBeenExceeded,
    superminsPointsFieldCannotBeEmpty,
    superminsPointsToSpendMustBePositiveIntegers,
    superminsPointsWillBeRedeemed
  } = messages

  let errors

  if (isSupermins) {
    errors = [
      { condition: superminsPointsToSpend === null, message: superminsPointsFieldCannotBeEmpty },
      {
        condition: !Number.isInteger(Number(superminsPointsToSpend)) || Number(superminsPointsToSpend) <= 0,
        message: superminsPointsToSpendMustBePositiveIntegers
      },
      {
        condition: superminsPointsToSpend > cartStore.maxSuperminsPointsForOrder,
        message: maximumNumberOfSuperminsPointsForOrderHasBeenExceeded
      }
    ]
  } else {
    errors = [
      { condition: pointsToSpend === null, message: pointsFieldCannotBeEmpty },
      {
        condition: !Number.isInteger(Number(pointsToSpend)) || Number(pointsToSpend) <= 0,
        message: pointsToSpendMustBePositiveIntegers
      },
      { condition: pointsToSpend > cartStore.maxPointsForOrder, message: maximumNumberOfPointsForOrderHasBeenExceeded }
    ]
  }

  for (const { condition, message } of errors) {
    if (condition) {
      $toast.error(message)

      return
    }
  }

  cartStore.isPointsApplying = true
  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  if (cartStore.isDataLoadingError) {
    $toast.error(isSupermins ? failedToApplySuperminsPoints : failedToApplyPoints)
  } else if (cartStore.errorDetail) {
    $toast.error(cartStore.errorDetail)
  } else {
    if (isSupermins) {
      cartStore.areSuperminsPointsApplied = true
    } else {
      cartStore.arePointsApplied = true
    }

    $toast.success(isSupermins ? superminsPointsWillBeRedeemed : pointsWillBeRedeemed)
  }

  cartStore.isDataLoading2 = false
  cartStore.isPointsApplying = false
}

async function applyPromoCode (): Promise<void> {
  const { promoCodeFieldCannotBeEmpty } = messages

  if (!cartStore.form.promoCodeName) {
    $toast.error(promoCodeFieldCannotBeEmpty)

    return
  }

  cartStore.isPromoCodeApplying = true
  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  cartStore.isDataLoading2 = false
  cartStore.isPromoCodeApplying = false
}

async function cancelCertificate (): Promise<void> {
  cartStore.isCertificateApplied = false
  cartStore.form.certificateName = null
  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  cartStore.isDataLoading2 = false
}

async function cancelPoints (isSupermins = false): Promise<void> {
  if (isSupermins) {
    cartStore.form.superminsPointsToSpend = cartStore.maxSuperminsPointsForOrder
    cartStore.areSuperminsPointsApplied = false
  } else {
    cartStore.form.pointsToSpend = cartStore.maxPointsForOrder
    cartStore.arePointsApplied = false
  }

  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  cartStore.isDataLoading2 = false
}

async function cancelPromoCode (): Promise<void> {
  cartStore.isPromoCodeApplied = false

  clearPromoCodeField()

  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  cartStore.isDataLoading2 = false
}

async function changeDiscountType (x: DiscountType): Promise<void> {
  const { pointsToSpend, promoCodeName, superminsPointsToSpend } = cartStore.form
  const { Points, PromoCode, SuperminsPoints } = DiscountType

  if (
    (discountType.value === PromoCode && promoCodeName && cartStore.isPromoCodeApplied)
    || (discountType.value === Points && pointsToSpend !== 0 && cartStore.arePointsApplied)
    || (discountType.value === SuperminsPoints && superminsPointsToSpend !== 0 && cartStore.areSuperminsPointsApplied)
  ) {
    await useModal({
      component: ConfirmDiscountSelection,
      attrs: {
        currentDiscountType: discountType.value,
        newDiscountType: x,
        onConfirmDiscountSelection: confirmDiscountSelection
      }
    }).open()

    return
  }

  if (discountType.value === PromoCode) {
    cartStore.form.promoCodeName = null
  }

  discountType.value = x
}

function clearPromoCodeField (): void {
  cartStore.form.promoCodeName = null

  useCookie('promoCode').value = null
}

async function confirmDiscountSelection (x: DiscountType): Promise<void> {
  const { pointsToSpend, promoCodeName, superminsPointsToSpend } = cartStore.form
  const { Points, PromoCode, SuperminsPoints } = DiscountType

  switch (discountType.value) {
    case Points:
      if (pointsToSpend !== 0 && cartStore.arePointsApplied) {
        await cancelPoints()
      }

      break
    case PromoCode:
      if (promoCodeName && cartStore.isPromoCodeApplied) {
        await cancelPromoCode()
      }

      break
    case SuperminsPoints:
      if (superminsPointsToSpend !== 0 && cartStore.areSuperminsPointsApplied) {
        await cancelPoints(true)
      }

      break
    default:
      break
  }

  if (discountType.value === PromoCode) {
    cartStore.form.promoCodeName = null
  }

  discountType.value = x
}
</script>

<template>
  <div>
    <div v-show="discountType === DiscountType.PromoCode">
      <BasicInputDecorator class="relative">
        <BasicInput
          v-model="cartStore.form.promoCodeName"
          class="[&_input]:!pr-10"
          :class="{ '[&_input]:!text-main-green': cartStore.isPromoCodeApplied }"
          :is-border="false"
          :is-disabled="cartStore.isPromoCodeApplied"
          placeholder="Промокод"
          @enter="applyPromoCode"
        />

        <a
          v-if="cartStore.isPromoCodeApplied"
          class="absolute right-3 top-[calc(50%-12px)]"
          href="#"
          @click.prevent="cancelPromoCode"
          @mouseenter="hoveredPromoCode = true"
          @mouseleave="hoveredPromoCode = false"
        >
          <SvgIcon v-if="hoveredPromoCode" class="!size-6 text-main-red" name="outlined/close" />
          <SvgIcon v-else class="!size-6 transition-colors hover:text-main-green" name="outlined/success-rounded" />
        </a>
        <a
          v-else-if="cartStore.form.promoCodeName"
          class="absolute right-3 top-[calc(50%-12px)]"
          href="#"
          @click.prevent="applyPromoCode"
        >
          <SvgIcon class="!size-6 transition-colors hover:text-main-green" name="outlined/enter" />
        </a>
      </BasicInputDecorator>

      <template v-if="cartStore.isPromoCodeApplied">
        <span class="mt-2 block text-sm leading-[140%] text-[#434242]">
          Применили скидку к&nbsp;вашему заказу
        </span>
      </template>
    </div>

    <div v-show="discountType === DiscountType.Points">
      <Notification class="mb-2 text-sm" is-small status="info">
        У&nbsp;вас&nbsp;{{ formatter.formatPrice(cartStore.userTotalPoints) }}&nbsp;{{ helpers.getDeclension(cartStore.userTotalPoints, constants.wordForms.point) }} iTAB Плюс. На&nbsp;этот заказ можно потратить не&nbsp;более&nbsp;{{ formatter.formatPrice(cartStore.maxPointsForOrder) }}.
      </Notification>

      <BasicInputDecorator class="relative">
        <BasicInput
          v-model.number="cartStore.form.pointsToSpend"
          v-maska="maskaOptions.number"
          class="mb-4"
          :class="{ '[&_input]:!text-main-green': cartStore.arePointsApplied }"
          :is-border="false"
          :is-disabled="cartStore.arePointsApplied"
          placeholder="Баллы iTAB Плюс для списания"
          @enter="applyPoints(false)"
        />

        <a
          v-if="cartStore.arePointsApplied"
          class="absolute right-3 top-[calc(50%-12px)]"
          href="#"
          @click.prevent="cancelPoints"
          @mouseenter="hoveredPoints = true"
          @mouseleave="hoveredPoints = false"
        >
          <SvgIcon v-if="hoveredPoints" class="!size-6 text-main-red" name="outlined/close" />
          <SvgIcon v-else class="!size-6 transition-colors hover:text-main-green" name="outlined/success-rounded" />
        </a>
        <a
          v-else-if="cartStore.form.pointsToSpend"
          class="absolute right-3 top-[calc(50%-12px)]"
          href="#"
          @click.prevent="applyPoints(false)"
        >
          <SvgIcon class="!size-6 transition-colors hover:text-main-green" name="outlined/enter" />
        </a>
      </BasicInputDecorator>

      <template v-if="cartStore.arePointsApplied">
        <span class="mt-2 block text-sm leading-[140%] text-[#434242]">
          На&nbsp;этот заказ вы&nbsp;потратите {{ formatter.formatPrice(cartStore.form.pointsToSpend) }}&nbsp;{{ helpers.getDeclension(cartStore.form.pointsToSpend, constants.wordForms.point) }} iTAB Плюс.
        </span>
      </template>
    </div>

    <div v-show="discountType === DiscountType.SuperminsPoints">
      <Notification class="mb-2 text-sm" is-small status="info">
        У&nbsp;вас&nbsp;{{ formatter.formatPrice(cartStore.userTotalSuperminsPoints) }}&nbsp;{{ helpers.getDeclension(cartStore.userTotalSuperminsPoints, constants.wordForms.point) }} Supermins. На&nbsp;этот заказ можно потратить не&nbsp;более&nbsp;{{ formatter.formatPrice(cartStore.maxSuperminsPointsForOrder) }}.
      </Notification>

      <BasicInputDecorator class="relative">
        <BasicInput
          v-model.number="cartStore.form.superminsPointsToSpend"
          v-maska="maskaOptions.number"
          class="mb-4"
          :class="{ '[&_input]:!text-main-green': cartStore.areSuperminsPointsApplied }"
          :is-border="false"
          :is-disabled="cartStore.areSuperminsPointsApplied"
          placeholder="Баллы Supermins для списания"
          @enter="applyPoints(true)"
        />

        <a
          v-if="cartStore.areSuperminsPointsApplied"
          class="absolute right-3 top-[calc(50%-12px)]"
          href="#"
          @click.prevent="cancelPoints(true)"
          @mouseenter="hoveredPoints2 = true"
          @mouseleave="hoveredPoints2 = false"
        >
          <SvgIcon v-if="hoveredPoints2" class="!size-6 text-main-red" name="outlined/close" />
          <SvgIcon v-else class="!size-6 transition-colors hover:text-main-green" name="outlined/success-rounded" />
        </a>
        <a
          v-else-if="cartStore.form.superminsPointsToSpend"
          class="absolute right-3 top-[calc(50%-12px)]"
          href="#"
          @click.prevent="applyPoints(true)"
        >
          <SvgIcon class="!size-6 transition-colors hover:text-main-green" name="outlined/enter" />
        </a>
      </BasicInputDecorator>

      <template v-if="cartStore.areSuperminsPointsApplied">
        <span class="mt-2 block text-sm leading-[140%] text-[#434242]">
          На&nbsp;этот заказ вы&nbsp;потратите {{ formatter.formatPrice(cartStore.form.superminsPointsToSpend) }}&nbsp;{{ helpers.getDeclension(cartStore.form.superminsPointsToSpend, constants.wordForms.point) }} Supermins.
        </span>
      </template>
    </div>

    <template v-if="discountTypes.length">
      <ul class="mt-2">
        <li v-for="x in discountTypes" :key="x">
          <a class="text-sm text-main-green" href="#" @click.prevent="changeDiscountType(x)">
            <template v-if="x === DiscountType.PromoCode">Применить промокод</template>
            <template v-else-if="x === DiscountType.Points">
              Потратить баллы iTAB Плюс (не&nbsp;более&nbsp;{{ formatter.formatPrice(cartStore.maxPointsForOrder) }})
            </template>
            <template v-else-if="x === DiscountType.SuperminsPoints">
              Потратить баллы Supermins (не&nbsp;более&nbsp;{{ formatter.formatPrice(cartStore.maxSuperminsPointsForOrder) }})
            </template>
          </a>
        </li>
      </ul>
    </template>

    <div v-if="showCertificateBlock">
      <BasicInputDecorator class="relative">
        <BasicInput
          v-model="cartStore.form.certificateName"
          class="mb-4 mt-2"
          :class="{ '[&_input]:!text-main-green': cartStore.isCertificateApplied }"
          :is-border="false"
          :is-disabled="cartStore.isCertificateApplied"
          placeholder="Код активации сертификата"
          @enter="applyCertificate"
        />

        <a
          v-if="cartStore.isCertificateApplied"
          class="absolute right-3 top-[calc(50%-12px)]"
          href="#"
          @click.prevent="cancelCertificate"
          @mouseenter="hoveredCertificate = true"
          @mouseleave="hoveredCertificate = false"
        >
          <SvgIcon v-if="hoveredCertificate" class="!size-6 text-main-red" name="outlined/close" />
          <SvgIcon v-else class="!size-6 transition-colors hover:text-main-green" name="outlined/success-rounded" />
        </a>
        <a
          v-else-if="cartStore.form.certificateName"
          class="absolute right-3 top-[calc(50%-12px)]"
          href="#"
          @click.prevent="applyCertificate"
        >
          <SvgIcon class="!size-6 transition-colors hover:text-main-green" name="outlined/enter" />
        </a>
      </BasicInputDecorator>

      <template v-if="cartStore.isCertificateApplied">
        <span class="mt-2 block text-sm leading-[140%] text-[#434242]">
          Применили сертификат
        </span>
      </template>
    </div>
    <div v-else>
      <a
        class="mt-2 inline-block text-sm text-main-green"
        href="#"
        @click.prevent="showCertificateBlock = !showCertificateBlock"
      >
        Применить сертификат
      </a>
    </div>
  </div>
</template>
